import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import TextInput from '@components/ui/Input/TextInput';
import { getUserById, updateUser } from '@/services/userService';
import { getRoles } from '@/services/roleService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { UpdatePlatformUserPayload } from '@/types/user';
import styles from './UserModals.module.scss';

// ============================================
// TYPES
// ============================================

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
}

interface EditUserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: string[];
}

// ============================================
// COMPONENT
// ============================================

function EditUserModal({ isOpen, onClose, userId }: EditUserModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Fetch user detail
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId!),
    enabled: isOpen && !!userId,
  });

  // Fetch available roles for the picker
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', 'pickerAll'],
    queryFn: () => getRoles({ page: 0, size: 100 }),
    enabled: isOpen,
  });

  const roleOptions = useMemo(
    () =>
      (rolesData?.content ?? []).map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [rolesData],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EditUserFormValues>({
    defaultValues: { firstName: '', lastName: '', email: '', roleIds: [] },
    mode: 'onChange',
  });

  // Pre-fill form when user data loads
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        roleIds: user.roles.map((r) => r.id),
      });
    }
  }, [user, reset]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({ firstName: '', lastName: '', email: '', roleIds: [] });
    }
  }, [isOpen, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePlatformUserPayload) => updateUser(userId!, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const onSubmit = useCallback(
    (data: EditUserFormValues) => {
      const trimmedEmail = data.email.trim();
      const payload: UpdatePlatformUserPayload = {
        firstName: data.firstName,
        lastName: data.lastName.trim() || undefined,
        email: trimmedEmail !== (user?.email ?? '') ? trimmedEmail : undefined,
        roleIds: data.roleIds,
      };
      updateMutation.mutate(payload);
    },
    [updateMutation, user?.email],
  );

  const handleClose = useCallback(() => {
    if (!updateMutation.isPending) {
      onClose();
    }
  }, [updateMutation.isPending, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_MODAL_EDIT_USER', 'Edit User')}
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="edit-user-form"
            disabled={updateMutation.isPending || userLoading}
          >
            {updateMutation.isPending
              ? t('K_BUTTON_SAVING', 'Saving...')
              : t('K_UPDATE', 'Update')}
          </Button>
        </div>
      }
    >
      {userLoading ? (
        <p>{t('K_LOADING', 'Loading...')}</p>
      ) : (
        <form
          id="edit-user-form"
          onSubmit={handleSubmit(onSubmit)}
          className={styles.form}
        >
          <div className={styles.row}>
            <TextInput
              label={t('K_LABEL_FIRST_NAME', 'First Name')}
              isRequired
              placeholder={t('K_PLACEHOLDER_FIRST_NAME', 'e.g. Jane')}
              error={errors.firstName?.message}
              {...register('firstName', {
                required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required'),
                maxLength: {
                  value: 255,
                  message: t('K_VALIDATION_MAX_255', 'Max 255 characters'),
                },
              })}
            />
            <TextInput
              label={t('K_LABEL_LAST_NAME', 'Last Name')}
              placeholder={t('K_PLACEHOLDER_LAST_NAME', 'e.g. Smith')}
              error={errors.lastName?.message}
              {...register('lastName', {
                maxLength: {
                  value: 255,
                  message: t('K_VALIDATION_MAX_255', 'Max 255 characters'),
                },
              })}
            />
          </div>

          <TextInput
            label={t('K_LABEL_EMAIL', 'Email')}
            isRequired
            type="email"
            placeholder={t('K_PLACEHOLDER_EMAIL', 'Enter email')}
            error={errors.email?.message}
            {...register('email', {
              required: t('K_VALIDATION_EMAIL_REQUIRED', 'Email is required'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('K_VALIDATION_EMAIL_PATTERN', 'Enter a valid email address'),
              },
            })}
          />

          <Controller
            name="roleIds"
            control={control}
            render={({ field }) => (
              <MultiSelect
                label={t('K_LABEL_ROLES', 'Roles')}
                placeholder={t('K_PLACEHOLDER_SELECT_ROLES', 'Select roles')}
                options={roleOptions}
                value={field.value}
                onChange={field.onChange}
                loading={rolesLoading}
                searchable
              />
            )}
          />
        </form>
      )}
    </Modal>
  );
}

export default EditUserModal;
